import { useState } from 'react';
import { Button, Input } from '@/shared/ui/atoms';
import * as s from './ManualCandidateAdd.css';

/** 방금 더한 결과 — 해소됐으면 계정 번호가 오고, 아니면 크롤러가 방문을 예약한 것이다. */
export interface ManualCandidateResult {
  handle: string;
  externalCreatorId: string | null;
  status: 'RESOLVED' | 'PENDING';
}

export interface ManualCandidateAddProps {
  onAdd: (input: string) => void;
  pending?: boolean;
  /** 마지막으로 더한 결과. 없으면 아무것도 안 그린다. */
  result?: ManualCandidateResult | null;
  /** 서버가 되돌려 준 거절 사유. 화면이 문구를 지어내지 않는다. */
  error?: string | null;
}

/**
 * 직접 추가 — IA `docs/design/ia/creator-recruitment-ia.md` §3-3 「수동 추가」.
 *
 * **주 CTA 가 아니다.** 검색 결과 아래에 붙는 보조 경로이고 핸들이나 프로필 링크를 **하나만**
 * 받는다. 대량 붙여넣기는 실제 수요가 확인될 때까지 두지 않는다.
 *
 * **해소 전에는 관계를 만들지 않는다.** 그래서 더한 직후의 두 답이 다르다 — 원천이 이미 아는
 * 계정이면 바로 후보가 되고, 모르면 크롤러가 프로필을 방문할 때까지 `확인 중` 이다. 이 구분을
 * 뭉개면 담당자가 아직 식별 안 된 사람을 발송 명단에 있다고 읽는다.
 *
 * **처음에는 글자 버튼 하나로 접혀 있다**(퀘스트 2f546c56 B안). 입력 칸이 늘 서 있으면 보조 경로가
 * 한 줄을 통째로 쓴다. 누르면 그 자리에 칸이 열리고, 더한 결과나 거절 사유가 있으면 열린 채 둔다.
 */
export function ManualCandidateAdd({
  onAdd,
  pending = false,
  result = null,
  error = null,
}: ManualCandidateAddProps) {
  const [raw, setRaw] = useState('');
  const [opened, setOpened] = useState(false);
  const trimmed = raw.trim();
  const canAdd = trimmed.length > 0 && !pending;

  if (!opened && !pending && result === null && error === null) {
    return (
      <Button variant="ghost" size="xs" onClick={() => setOpened(true)}>
        찾는 크리에이터가 없나요? 직접 추가
      </Button>
    );
  }

  return (
    <div className={s.wrap}>
      <div className={s.row}>
        <label className={s.label} htmlFor="manual-candidate">
          찾는 크리에이터가 없나요?
        </label>
        <Input
          id="manual-candidate"
          inputSize="xs"
          className={s.input}
          placeholder="핸들 또는 프로필 링크 하나"
          value={raw}
          onChange={(event) => setRaw(event.target.value)}
          disabled={pending}
        />
        <Button
          variant="secondary"
          size="xs"
          disabled={!canAdd}
          onClick={() => {
            onAdd(trimmed);
            setRaw('');
          }}
        >
          {pending ? '확인하는 중' : '직접 추가'}
        </Button>
      </div>

      {error !== null ? (
        <p className={s.error}>{error}</p>
      ) : result !== null ? (
        <p className={result.status === 'RESOLVED' ? s.resolved : s.awaiting}>
          {result.status === 'RESOLVED'
            ? `${result.handle} 을(를) 후보에 더했습니다.`
            : `${result.handle} 은(는) 원천에서 확인하는 중입니다. 확인되면 후보로 올라옵니다.`}
        </p>
      ) : null}
    </div>
  );
}
